/**
 * Observed connection health — the third state `navigator.onLine` cannot express.
 *
 * Originally built for the Technician PWA (#4021) and lived at
 * `client-tech/src/lib/connectionHealth.ts`. Moved here (lane-tech-offline-data,
 * #1871) so shared hooks/pages — `useServiceReportLauncher.ts`,
 * `TechJobDetail.tsx` — can distinguish "genuinely offline" from "the query ran
 * and found nothing" without importing a client-tech-only path that does not
 * exist for the main `client` build. `client-tech/src/lib/connectionHealth.ts`
 * now re-exports this module unchanged, so its existing importers
 * (OfflineIndicator.tsx, syncManager.ts) are unaffected.
 *
 * THE DEFECT THIS CLOSES. Connectivity in the tech app was a BINARY read of
 * `navigator.onLine`. That flag reports whether the device has *an interface*,
 * not whether traffic is *getting through*. This was measured, not assumed:
 * against `technician.onpointgen.com` throttled to 20 kbps / 3000 ms latency,
 * `navigator.onLine` stayed `true` for the full 60 s. So on a degraded link the
 * app never enters its offline path, never shows the offline banner, and never
 * reaches its own working offline queue. The tech sees a normal-looking app.
 *
 * WHAT THIS IS NOT. It is NOT a timeout mechanism. The same measurement showed
 * 88 of 94 requests completing on that 20 kbps link — nothing hung — and the
 * Firebase SDK brings its own retry, backoff and connection management. An
 * app-level abort was proposed and DROPPED because the hang it would fix was
 * never observed in production.
 *
 * WHERE THE SIGNAL COMES FROM. Real outcomes of real operations: an upload that
 * rejects is a failure, one that completes a full round trip is a success. The
 * one exception is the tech app's connection probe (client-tech/src/lib/connectionProbe.ts):
 * a bounded 5 s request, so it cannot hang on the link it measures. Never a guess
 * from signal strength.
 *
 * HYSTERESIS, asymmetric on purpose. It takes {@link STALL_THRESHOLD} consecutive
 * failures with no interleaved success to report a degraded link, and exactly ONE
 * success to clear it. Declaring degradation is a user-visible claim that should
 * be reliable; clearing it is proven by a completed round trip.
 *
 * SHARED-MODULE RULE this file obeys (see telemetry/sentryEnvironmentGate.ts):
 * `process.env.NODE_ENV`, never `import.meta.env` — `import.meta` is a hard
 * SyntaxError under CRA's jest transform, and this module is imported (directly
 * or transitively) by main-`client` jest suites. `process.env.NODE_ENV` works
 * natively under CRA/jest and is shimmed for Vite by client-tech's envShim.ts.
 */

/** Consecutive failures (no success between) before the link is called degraded. */
export const STALL_THRESHOLD = 2;

type StallListener = (stalled: boolean) => void;

let consecutiveFailures = 0;
let stalled = false;
const listeners = new Set<StallListener>();

function emit() {
  listeners.forEach((fn) => fn(stalled));
}

/**
 * Record that an operation failed. After {@link STALL_THRESHOLD} in a row the
 * connection is reported degraded.
 */
export function reportFailure(): void {
  consecutiveFailures += 1;
  if (!stalled && consecutiveFailures >= STALL_THRESHOLD) {
    stalled = true;
    emit();
  }
}

/** Record that an operation completed a full round trip. Clears any degradation. */
export function reportSuccess(): void {
  consecutiveFailures = 0;
  if (stalled) {
    stalled = false;
    emit();
  }
}

/** True when our own operations are failing despite `navigator.onLine`. */
export function isConnectionStalled(): boolean {
  return stalled;
}

/**
 * The check that should gate user-facing connectivity messaging, replacing a
 * bare `navigator.onLine`.
 *
 * Deliberately NOT used to gate the sync loop: the drain attempt is the only
 * thing that can produce the success which clears a degraded reading, so gating
 * retries on it would latch the state permanently with nothing able to clear it.
 */
export function isEffectivelyOffline(): boolean {
  return !navigator.onLine || stalled;
}

/** Subscribe to transitions. Emits current state immediately. Returns unsubscribe. */
export function onConnectionStallChange(callback: StallListener): () => void {
  listeners.add(callback);
  callback(stalled);
  return () => { listeners.delete(callback); };
}

/**
 * Reset to the initial state. Tests only — a module-level singleton would
 * otherwise leak state between cases. No-op in production builds so it cannot
 * clear a real tech's state if it survives tree-shaking.
 */
export function __resetConnectionHealthForTests(): void {
  if (process.env.NODE_ENV === 'production') return;
  consecutiveFailures = 0;
  stalled = false;
  listeners.clear();
}
